import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { MultiSelectModule } from 'primeng/multiselect';
import { firstValueFrom } from 'rxjs';
import { TicketService } from '@app/features/v3ndor/core/services/ticket.service';
import { SavedFilter } from '@app/features/v3ndor/core/models/saved-filter.model';
import {
  SavedFilterDialogComponent,
  SavedFilterFormValue,
} from '@app/features/v3ndor/components/saved-filter-dialog/saved-filter-dialog.component';
import {
  PERIODO_LABELS,
  PERIODO_PADRAO,
  PeriodoAtalho,
  TicketLogFilterParams,
  formatarISO,
  resolvePeriodo,
} from '../ticket-log-filter-params';
import {
  QuickDateRangeComponent,
  QuickDateRangeSelection,
} from 'src/app/shared/components/quick-date-range/quick-date-range.component';

interface SelectOption {
  label: string;
  value: string;
}

/**
 * Modal de filtros do histórico de apontamentos, no padrão novo do v3ndor.
 * Mesma forma do `support-ticket-filter-panel` — o precedente deste padrão
 * dentro de um NgModule legado.
 *
 * ## Obrigatoriedade
 *
 * A tela antiga só reclamava DEPOIS do clique em Buscar, por toast. Aqui o
 * botão fica desabilitado enquanto faltar usuário ou data, com a razão escrita
 * ao lado — a diferença entre "o sistema recusou" e "eu sei o que falta".
 */
@Component({
  selector: 'app-ticket-log-filter-panel',
  standalone: true,
  imports: [
    FormsModule,
    MultiSelectModule,
    DatePickerModule,
    ButtonModule,
    DialogModule,
    SavedFilterDialogComponent,
    QuickDateRangeComponent,
  ],
  templateUrl: './ticket-log-filter-panel.component.html',
  styleUrls: ['./ticket-log-filter-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketLogFilterPanelComponent implements OnInit {
  private readonly ticketService = inject(TicketService);

  @Input() visible = false;
  @Input() loading = false;
  @Input() enableSavedFilters = false;

  @Input() set filters(value: TicketLogFilterParams) {
    const f = value ?? {};
    this.userIds.set(f.user_ids ?? []);
    this.customerIds.set(f.customer_ids ?? []);

    // Com atalho ativo as datas não ficam guardadas no filtro — resolvê-las aqui
    // é o que faz o painel abrir mostrando o período que está de fato em uso, em
    // vez de dois campos vazios.
    const periodo = resolvePeriodo(f, new Date());
    this.dateFrom.set(periodo ? this.paraData(periodo.from) : null);
    this.dateTo.set(periodo ? this.paraData(periodo.to) : null);

    // Exibir as datas resolvidas NÃO é o mesmo que a pessoa tê-las escolhido.
    // Guardar o modo de origem e zerar o flag a cada abertura é o que permite
    // `buildFilters` devolver o atalho intacto quando só outros campos mudaram.
    this.periodoRecebido.set(f.period ?? PERIODO_PADRAO);
    this.datasTocadas.set(false);

    // Semeia as listas com o que já está selecionado. Sem isto o multiselect
    // abriria mostrando ids crus (ou nada) até a busca do servidor responder —
    // e o filtro restaurado do storage pareceria vazio.
    this.userOptions.set(this.semear(f.user_ids, f.user_labels));
    this.customerOptions.set(this.semear(f.customer_ids, f.customer_labels));
  }

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() searchFilters = new EventEmitter<TicketLogFilterParams>();
  @Output() saveFilter = new EventEmitter<SavedFilter<TicketLogFilterParams>>();

  readonly userIds = signal<string[]>([]);
  readonly customerIds = signal<string[]>([]);
  readonly dateFrom = signal<Date | null>(null);
  readonly dateTo = signal<Date | null>(null);

  readonly userOptions = signal<SelectOption[]>([]);
  readonly customerOptions = signal<SelectOption[]>([]);
  readonly usersLoading = signal(false);
  readonly customersLoading = signal(false);

  /** Modo que o filtro tinha ao abrir o painel. Preservado se as datas não mudarem. */
  readonly periodoRecebido = signal<PeriodoAtalho>(PERIODO_PADRAO);
  /** `true` só depois de a pessoa mexer num dos datepickers. */
  readonly datasTocadas = signal(false);

  /** Modo dinâmico ainda intacto — o que a dica abaixo dos campos comunica. */
  readonly periodoDinamicoAtivo = computed(
    () => !this.datasTocadas() && this.periodoRecebido() !== 'custom'
  );

  readonly periodoLabels = PERIODO_LABELS;

  saveDialogVisible = false;

  /** Chamado pelos datepickers: escolher data à mão fixa o intervalo. */
  onDateChange(qual: 'from' | 'to', valor: Date | null): void {
    if (qual === 'from') this.dateFrom.set(valor);
    else this.dateTo.set(valor);
    this.datasTocadas.set(true);
  }

  /**
   * Seleção rápida e "Limpar datas". Contam como escolha manual: fixam o intervalo
   * (`period: 'custom'`), igual a digitar as datas.
   */
  onQuickPeriod({ start, end }: QuickDateRangeSelection): void {
    this.onDateChange('from', start);
    this.onDateChange('to', end);
  }

  onClearDates(): void {
    this.onDateChange('from', null);
    this.onDateChange('to', null);
  }

  ngOnInit(): void {
    void this.buscarUsuarios('');
  }

  /** Motivo pelo qual a busca está bloqueada, ou `null` quando está liberada. */
  get impedimento(): string | null {
    if (!this.userIds().length) return 'Selecione ao menos um usuário.';
    if (!this.dateFrom() || !this.dateTo()) return 'Informe a data inicial e a final.';
    if (this.dateFrom()! > this.dateTo()!) return 'A data inicial é posterior à final.';
    return null;
  }

  async onUserFilter(event: { filter: string }): Promise<void> {
    await this.buscarUsuarios(event.filter ?? '');
  }

  // Cliente só busca no servidor: a base é grande demais para vir inteira, e um
  // multiselect com milhares de linhas não é utilizável.
  async onCustomerFilter(event: { filter: string }): Promise<void> {
    const termo = (event.filter ?? '').trim();
    if (termo.length < 3) return;
    await this.buscarClientes(termo);
  }

  /**
   * Escolher datas na mão produz `period: 'custom'`: mesmo que o intervalo
   * digitado coincida com a semana atual, o que a pessoa pediu foi um recorte
   * fixo — manter o modo dinâmico faria as datas mudarem sozinhas na semana
   * seguinte, o oposto do que ela acabou de fazer.
   *
   * Mas o inverso também vale, e antes não valia: mexer SÓ no cliente (ou no
   * usuário) e clicar Buscar não pode matar o atalho. O painel exibe as datas
   * que o atalho resolveu, e tratar essa exibição como escolha manual congelava
   * o período em silêncio — inclusive ao salvar um filtro, que assim nunca
   * conseguia nascer dinâmico.
   */
  private buildFilters(): TicketLogFilterParams {
    const fixarDatas = this.datasTocadas() || this.periodoRecebido() === 'custom';

    const filtros: TicketLogFilterParams = {
      user_ids: this.userIds(),
      user_labels: this.rotulosDe(this.userOptions(), this.userIds()),
      period: fixarDatas ? 'custom' : this.periodoRecebido(),
      // As datas só entram no modo fixo: em 'week'/'month' elas são derivadas de
      // "hoje" a cada consulta, e gravá-las deixaria no storage um valor que
      // envelhece — é o mesmo cuidado que `onPeriodChange` toma ao descartá-las.
      ...(fixarDatas
        ? { date_from: formatarISO(this.dateFrom()!), date_to: formatarISO(this.dateTo()!) }
        : {}),
    };

    if (this.customerIds().length) {
      filtros.customer_ids = this.customerIds();
      filtros.customer_labels = this.rotulosDe(this.customerOptions(), this.customerIds());
    }

    return filtros;
  }

  openSaveDialog(): void {
    this.saveDialogVisible = true;
  }

  onSaveDialogVisibleChange(visible: boolean): void {
    this.saveDialogVisible = visible;
  }

  onSaveFilterConfirm(value: SavedFilterFormValue): void {
    this.saveFilter.emit({ ...value, params: this.buildFilters() });
  }

  onSearch(): void {
    if (this.impedimento) return;

    const filtros = this.buildFilters();

    this.searchFilters.emit(filtros);
    this.close();
  }

  /**
   * Limpa só o que é opcional. Zerar usuário e período deixaria a tela num
   * estado que a própria busca recusa — "limpar" viraria "travar".
   */
  onClear(): void {
    this.customerIds.set([]);
  }

  close(): void {
    this.visibleChange.emit(false);
  }

  private async buscarUsuarios(termo: string): Promise<void> {
    this.usersLoading.set(true);
    const itens = await firstValueFrom(this.ticketService.searchUsers(termo)).catch(() => []);
    this.userOptions.set(this.mesclar(this.userOptions(), itens, this.userIds()));
    this.usersLoading.set(false);
  }

  private async buscarClientes(termo: string): Promise<void> {
    this.customersLoading.set(true);
    const itens = await firstValueFrom(this.ticketService.searchCustomers(termo)).catch(() => []);
    this.customerOptions.set(this.mesclar(this.customerOptions(), itens, this.customerIds()));
    this.customersLoading.set(false);
  }

  /**
   * Junta o resultado da busca com as opções JÁ SELECIONADAS. Sem isso, buscar
   * um nome novo apagaria da lista os que a pessoa acabou de marcar — e o
   * `p-multiselect` mostraria a seleção como ids soltos.
   */
  private mesclar(atuais: SelectOption[], novos: any[], selecionados: string[]): SelectOption[] {
    const mantidos = atuais.filter(o => selecionados.includes(o.value));
    const vistos = new Set(mantidos.map(o => o.value));
    const mapeados = novos
      .map(item => ({ label: item?.name ?? item?.description ?? '', value: item?.id ?? '' }))
      .filter(o => o.value && !vistos.has(o.value));
    return [...mantidos, ...mapeados];
  }

  private semear(ids: string[] | undefined, labels: string[] | undefined): SelectOption[] {
    if (!ids?.length) return [];
    return ids.map((id, i) => ({ value: id, label: labels?.[i] ?? id }));
  }

  private rotulosDe(opcoes: SelectOption[], ids: string[]): string[] {
    return ids.map(id => opcoes.find(o => o.value === id)?.label ?? id);
  }

  /** `AAAA-MM-DD` como hora local: `new Date('2026-08-24')` seria UTC e voltaria um dia. */
  private paraData(iso: string): Date {
    return new Date(`${iso}T00:00:00`);
  }
}
